import messagingService from 'services/MessagingService/MessagingService';
import {
  HLS_DEBUG,
  SEND_VIDEO_METRICS,
  UPDATE_VIDEO_METRICS,
} from 'services/MessagingService/EventNames';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore, { STREAMING_SOURCE_TYPES } from 'stores/ProfileStore';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import { logger } from 'rum';


const childLogger = logger;

/**
 * Problem:
 *  actual SmartxPlayer component uses inside a few instances of common services
 *  also passes component props AND dom elements for services
 *  and allowed to modify they inside these services
 *
 *  So really SmartxPlayer is not a player, is a facade of a few services/controllers/etc
 *  That also exposes some methods for UI controls, Video controls, or browser at all.
 *
 *  Because of that, we are not allowed just to use "another player"
 *  we still have to use some features from SmartXPlayer
 *  because it's a monolithic part of rewrite app at all
 *
 *
 * Explanation:
 *  Please move all rewrite-app application related stuff (not UI part)
 *  to some common Facade (PlayerServicesFacade)
 *
 *  And use this facade to join some complex functionality of events/callbacks/app services
 *
 *  This facade should have some [common-named] methods
 *  that will work for SmartxPlayer AND JinkPlayer
 *
 *  This facade know difference between players and which player we are really using
 *  Player should know nothing about some application-related stuff and just use this facade
 *
 *
 * Migration from SmartxPlayer to JinkPlayer:
 *  Move step by step any application-related functionality from SmartxPlayer to this facade
 *  And use that functionality inside JinkPlayer
 *
 *  TODO:
 *    - First iteration of facade usage super primitive
 *        After that more properties will be moved to facade
 *        e.x. props/etc (to be able to create events data/etc inside facade)
 *    - Add player type to the events/metrics
 */

class MigratedPlayerService {
  constructor({ isJinkPlayer }) {
    this.isJinkPlayer = Boolean(isJinkPlayer);
    this.messagingService = messagingService();
  }

  get isSmartxPlayer() {
    return this.isJinkPlayer === false;
  }

  // old functionality componentDidMount of smartx player
  construct() {
    this.messagingService.emit(HLS_DEBUG, { method: 'player::componentDidMount' });
  }

  // old functionality componentWillUnmount of smartx player
  destroy() {
    this.messagingService.emit(HLS_DEBUG, { method: 'player::componentWillUnmount' });
  }

  // old functionality componentDidUpdate
  // some common method will be detailed later
  update({ isJinkPlayer, event }) {
    // TODO isJinkPlayer changed?
    this.isJinkPlayer = isJinkPlayer;
    this.messagingService.emit(HLS_DEBUG, { ...(event || {}) });
  }

  // see smartxplayer handleCustomVideoMetrics
  updateVideoMetrics(payload) {
    this.messagingService.emit(UPDATE_VIDEO_METRICS, payload);
  }

  // see smartxplayer handleTrackMetrics
  sendVideoMetrics(payload) {
    // event handling will be done from external JS code
    this.messagingService.emit(SEND_VIDEO_METRICS, payload);
  }

  // old getStreaminfoAndReconnect
  // TODO probably deprecated method
  reconnect({ event } = {}) {
    this.messagingService.emit(HLS_DEBUG, {
      method: 'player::getStreamingInfoAndReconnect',
      details: event.details,
      reason: event.reason,
    });
  }

  // request streaminfo/re init firebase room
  // do everything to restart viewer connection
  // and finally receive correct streaming data
  reconnectViewer() {
    this.messagingService.emit(HLS_DEBUG, {
      method: 'MigratedPlayerService::reconnectViewer',
      isStreamingSaas: FirebaseStore.isStreamingSaas,
    });

    if (ProfileStore.videoSourceType === STREAMING_SOURCE_TYPES.LLHLS) {
      childLogger.debug('[rewrite] Fallback to HLS stream');
      // Instead of reconnect LLHLS we need to fallback to HLS stream
      ProfileStore.getStreamingInfo(undefined, true);
    } else if (!FirebaseStore.isStreamingSaas) {
      childLogger.debug('[rewrite] Reload the stream');
      ProfileStore.getStreamingInfo();
    } else if (ProfileStore.broadcastStatus !== BROADCAST_STATUS.INSIDE_GS) {
      FirebaseStore.handleReInitRoom();
    }
  }
}

export default MigratedPlayerService;
